import { isFieldRequired, setSessionStorage, toCamelCase } from '../../../Helpers/Util'
import { FieldsToModify } from '../../../Models/CommonListHeadingModal'
import { SegmentOption, SegmentPageResult } from '../../../Models/SegmentOptionModel'

export interface FormDataParamMaps {
  parameterMap: Map<string, any>
  requiredMap: Map<string, Map<string, string>>
}

export const buildFormDataParamMaps = (dynamicFormData: any[]): FormDataParamMaps => {
  const parameterMap = new Map<string, any>()
  const requiredMap = new Map<string, Map<string, string>>()

  dynamicFormData?.forEach((form: any) => {
    if (form.parameter?.fieldType?.toLowerCase() !== 'complextype' || !form.parameterRef) return
    const parentFieldName = form.parameter?.fieldName
    form.parameterRef.forEach((refField: any) => {
      const fieldName = toCamelCase(
        refField.parameter?.fieldName || refField.parameterId?.fieldName,
      )
      parameterMap.set(fieldName, refField.parameter || refField.parameterId)
      if (!requiredMap.has(fieldName)) requiredMap.set(fieldName, new Map())
      requiredMap.get(fieldName)?.set(parentFieldName, refField.required?.toLowerCase())
    })
  })

  return { parameterMap, requiredMap }
}

export const buildComplexFieldsFromSchema = (
  dynamicFormData: any[],
): { [key: string]: { id: number; fields: any[] }[] } => {
  const initialComplexFields: { [key: string]: { id: number; fields: any[] }[] } = {}

  dynamicFormData?.forEach((form: any) => {
    if (form.parameter?.fieldType?.toLowerCase() !== 'complextype' || !form.parameterRef?.length)
      return
    const complexFieldName = toCamelCase(form.parameter.fieldName)
    initialComplexFields[complexFieldName] = [
      {
        id: 1,
        fields: form.parameterRef.map((refField: any) => ({
          ...refField,
          parameter: refField.parameter || refField.parameterId,
          required: refField.required || (refField.mandatory ? 'yes' : 'no'),
        })),
      },
    ]
  })

  return initialComplexFields
}

export const processCategoryFormField = (
  form: any,
  initialComplexFields: { [key: string]: { id: number; fields: any[] }[] },
  parameterMap: Map<string, any>,
  requiredMap: Map<string, Map<string, string>>,
  checkIsFieldSingleSelect: (name: string) => boolean,
  isCategoryFormDataUpdated: boolean,
  setValue: (name: string, value: any) => void,
) => {
  const fieldType = form.parameterId?.fieldType?.toLowerCase()
  const fieldName = form.parameterId?.fieldName

  if (fieldType === 'complextype' && form.parameterRef?.length > 0) {
    const complexFieldName = toCamelCase(fieldName)
    initialComplexFields[complexFieldName] = form.parameterRef.map(
      (refGroup: any[], index: number) => ({
        id: index + 1,
        fields: refGroup.map((refField: any) => {
          const refFieldName = toCamelCase(refField.parameterId?.fieldName)
          const requiredStatus = requiredMap.get(refFieldName)?.get(complexFieldName)
          setValue(`${complexFieldName}_${index + 1}_${refFieldName}`, refField.value || '')
          return {
            ...refField,
            parameter: parameterMap.get(refFieldName) || refField.parameterId,
            required: requiredStatus,
          }
        }),
      }),
    )
    return
  }

  if (
    fieldName?.toLowerCase() === 'segment' &&
    checkIsFieldSingleSelect(fieldName) &&
    isCategoryFormDataUpdated
  ) {
    setValue(fieldName, form?.value?.[0]?.key ?? '')
    setSessionStorage('singleSelectSegment', form?.value ?? [])
    return
  }

  if (fieldName?.toLowerCase() === 'asset_types' && isCategoryFormDataUpdated) {
    const isSingleSelect = checkIsFieldSingleSelect(fieldName)
    setValue(fieldName, isSingleSelect ? (form?.value?.[0]?.key ?? '') : (form?.value ?? []))
    setSessionStorage('assetTypes', form?.value ?? [])
    return
  }

  if (isCategoryFormDataUpdated) {
    setValue(fieldName, form?.value)
  }
}

export const mapSegmentRecord = (item: any): SegmentOption => ({
  key: item.segmentId,
  value: item.segmentName,
  segmentUrl: `/manage-segment/view/${item._id}`,
  irisStatus: item?.irisSegmentStatus,
})

export const normalizeSegmentPageResponse = (result: any): SegmentPageResult => {
  const records: any[] = result.data.data ?? []
  const total = result?.data?.count ?? 0

  return {
    data: records.map(mapSegmentRecord),
    total,
  }
}

export const mapApiResultToOptions = (
  apiUrl: string,
  result: any,
): { key: string; value: string }[] => {
  const updatedData: { key: string; value: string }[] = []

  if (apiUrl.includes('/segment/getAllSegmentName')) {
    result?.data?.forEach((item: any) =>
      updatedData.push({
        key: item.segmentId,
        value: item.segmentName,
        segmentUrl: `/manage-segment/view/${item._id}`,
        irisStatus: item?.irisSegmentStatus,
      } as any),
    )
  } else if (apiUrl.includes('/system-configurations/list/BLITZ_ATTRIBUTES')) {
    result?.data?.forEach((item: any) =>
      updatedData.push({ key: item.additionalField, value: item.value }),
    )
  } else if (apiUrl.includes('/country/list')) {
    result?.data?.records?.forEach((item: any) =>
      updatedData.push({ key: item.key, value: item.value }),
    )
  } else if (apiUrl.includes('/platform/list')) {
    result?.data?.records?.forEach((item: any) =>
      updatedData.push({ key: item.value, value: item.value }),
    )
  }

  return updatedData
}

export const resolveFieldDisabled = (parameter: any, fieldsToModify?: FieldsToModify): boolean => {
  if (parameter?.isDisabled) return true
  return !!(
    fieldsToModify?.disabledFields?.length &&
    fieldsToModify.disabledFields.includes(parameter?.fieldName)
  )
}

export const resolveFieldMandatory = (
  required: string,
  fieldName: string,
  fieldsToModify?: FieldsToModify,
): boolean => {
  if (
    fieldsToModify?.nonMandatoryFields?.length &&
    fieldsToModify.nonMandatoryFields.includes(fieldName)
  )
    return false
  return isFieldRequired(required)
}
